/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { ChangeEvent, memo, useState } from "react";

import {
  ChangeHandler,
  Controller,
  FormProvider,
  useForm,
  useFormState,
} from "react-hook-form";

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Inline,
  RadioGroup,
  SelectField,
  Stack,
  Text,
  TextField,
} from "@origin-digital/ods-core";
import { SelectFieldProps } from "@origin-digital/ods-core/dist/typings/SelectField/SelectField";
import { IconError } from "@origin-digital/ods-icons";
import { TileButtonGroup } from "@origin-digital/ods-tile-button";
import { ResponsiveProp } from "@origin-digital/ods-types";

import { createRadioOptions } from "@/utils";
import { yupResolver } from "@hookform/resolvers/yup";

import { StyledHelperText } from "./StyledText";
import { StyledTileButton } from "./StyledTileButton";

const alertMessageBackgroundColor = "#feebeb";

const ErrorMessage = memo(function ErrorMessage({ name }: { name: string }) {
  const {
    errors: { [name]: error },
  } = useFormState({ name });

  return error ? (
    <>
      <Box
        alignItems="center"
        borderRadius="medium"
        display="flex"
        padding="medium"
        backgroundColor={alertMessageBackgroundColor}
        marginY={["medium", "large"]}
      >
        <IconError
          size="small"
          muiProps={{ style: { marginRight: 8 } }}
          color="critical"
        />
        <Text tone="neutral">{(error as any).message}</Text>
      </Box>
    </>
  ) : (
    <></>
  );
});

const TextInputControl = React.forwardRef(function TextInput(
  {
    id,
    onChange,
    onBlur,
    name,
    label,
    helperText,
    defaultValue,
    ...rest
  }: {
    id: string;
    onChange: ChangeHandler;
    onBlur: ChangeHandler;
    name: string;
    label: string;
    helperText?: string;
    defaultValue?: string | number;
  },
  ref: any
) {
  const {
    errors: { [name]: error },
  } = useFormState({ name });

  return (
    <>
      <TextField
        id={id}
        onChange={onChange}
        onBlur={onBlur}
        name={name}
        label={label}
        inputRef={ref}
        error={!!error}
        helperText={error ? error.message?.toString() : helperText}
        defaultValue={defaultValue}
        {...rest}
      />
    </>
  );
});

const TextInput = memo(TextInputControl);

const CheckboxGroupInputControl = ({
  id,
  name,
  label,
  options,
  ...rest
}: {
  id: string;
  name: string;
  label: string;
  options: any;
}) => {
  return (
    <Controller
      name={name}
      render={({
        field: { onChange, value: values },
        fieldState: { error },
      }) => {
        const handleCheckChange = (
          event: ChangeEvent<HTMLInputElement>,
          checked: boolean
        ) => {
          if (checked) {
            if (values && !values.includes(event.target.value)) {
              onChange([...values, event.target.value]);
            } else {
              onChange([event.target.value]);
            }
          } else {
            const newValues = values?.filter(
              (val: any) => val !== event.target.value
            );
            onChange(newValues);
          }
        };

        return (
          <>
            <CheckboxGroup
              {...rest}
              id={id}
              label={label}
              error={!!error}
              helperText={error ? error.message : ""}
            >
              {Object.keys(options).map((key) => (
                <Checkbox
                  key={options[key].value}
                  id={options[key].value}
                  data-id={options[key].value}
                  value={options[key].value}
                  label={options[key].text}
                  checked={values?.includes(options[key].value)}
                  onChange={handleCheckChange}
                />
              ))}
            </CheckboxGroup>
          </>
        );
      }}
    />
  );
};

const CheckBoxGroupInput = memo(CheckboxGroupInputControl);

const SelectInputControl = React.forwardRef(function SelectInput(
  {
    onChange,
    id,
    name,
    label,
    options,
    ...rest
  }: {
    onChange: ChangeHandler;
    id: string;
    name: string;
    label: string;
    options: any;
  } & SelectFieldProps,
  ref: any
) {
  const {
    errors: { [name]: error },
  } = useFormState({ name });

  return (
    <>
      <SelectField
        id={id}
        name={name}
        label={label}
        options={options}
        onChange={onChange}
        hideHelperTextSpace={true}
        inputRef={ref}
        error={!!error}
        helperText={error ? error.message?.toString() : ""}
        {...rest}
      />
    </>
  );
});

const SelectInput = memo(SelectInputControl);

const RadioGroupInputControl = ({
  name,
  label,
  options,
  direction = "column",
  handleOnChange = undefined,
  ...rest
}: {
  name: string;
  label: string;
  options: any;
  direction?: string;
  handleOnChange?: (value: any) => void;
}) => {
  const {
    errors: { [name]: error },
  } = useFormState({ name });
  const radioOptions = createRadioOptions(options);

  return (
    <Controller
      name={name}
      render={({ field: { onChange, value } }) => {
        const handleRadioGroupChange = (
          _: ChangeEvent<HTMLInputElement>,
          radioValue?: string
        ) => {
          onChange(radioValue);

          if (handleOnChange) {
            handleOnChange(radioValue);
          }
        };

        return (
          <>
            <RadioGroup
              {...rest}
              name=""
              value={value}
              label={label}
              error={!!error}
              helperText={error ? error.message?.toString() : ""}
              onChange={handleRadioGroupChange}
            >
              {direction === "column" ? (
                <>{radioOptions}</>
              ) : (
                <Inline space="medium">{radioOptions}</Inline>
              )}
            </RadioGroup>
          </>
        );
      }}
    />
  );
};

const RadioGroupInput = memo(RadioGroupInputControl);

const TileButtonGroupInputControl = ({
  name,
  label,
  options,
  icons,
  cols,
  handleOnChange = undefined,
  ...rest
}: {
  name: string;
  label?: string;
  options: any;
  icons?: any;
  cols: ResponsiveProp<number>;
  handleOnChange?: (value: any) => void;
}) => {
  const {
    errors: { [name]: error },
  } = useFormState({ name });
  const [showError, setShowError] = useState(true);

  return (
    <Controller
      name={name}
      render={({ field: { onChange, value } }) => {
        const handleTileButtonGroupChange = (
          _: React.MouseEvent<HTMLElement>,
          tileButtonValue?: string
        ) => {
          onChange(tileButtonValue);
          setShowError(false);
          if (handleOnChange) {
            handleOnChange(tileButtonValue);
          }
        };

        return (
          <>
            <TileButtonGroup
              {...rest}
              id={name}
              label={label}
              cols={cols}
              onChange={handleTileButtonGroupChange}
              error={showError}
              helperText={
                error && showError ? (
                  <StyledHelperText>
                    {error.message?.toString()}
                  </StyledHelperText>
                ) : (
                  ""
                )
              }
            >
              {Object.keys(options).map((key, index) => (
                <StyledTileButton
                  key={options[key].value}
                  id={options[key].value}
                  value={options[key].value}
                  selected={
                    value ? options[key].value === value : options[key].selected
                  }
                >
                  <Stack alignX="center" space="xsmall">
                    {icons[index]}
                    <Text align="center">{options[key].text}</Text>
                  </Stack>
                </StyledTileButton>
              ))}
            </TileButtonGroup>
          </>
        );
      }}
    />
  );
};

const TileButtonGroupInput = memo(TileButtonGroupInputControl);

/**
 * Render a Form using Yup as Schema Validation. The form includes a Submit button
 * @param param0
 * @returns
 */
const YupForm = ({
  submit,
  schema,
  defaultValues,
  children,
}: {
  submit?: (data: any) => void;
  schema: any;
  defaultValues: any;
  children: JSX.Element;
}) => {
  const methods = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues,
  });

  const onSubmit = (data: any) => {
    if (submit) submit(data);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {children}
          {submit && <Button type="submit">Submit</Button>}
        </form>
      </FormProvider>
    </>
  );
};

export {
  CheckBoxGroupInput,
  ErrorMessage,
  RadioGroupInput,
  SelectInput,
  TextInput,
  TileButtonGroupInput,
  YupForm,
};
